import axios from 'axios'
import last from 'lodash/last'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import 'maplibre-gl/dist/maplibre-gl.css'
import { getSmartPlatform } from '@weathernews/btos-javascript-modules/src/wniutil/common.js'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const iconImages = {
  river_c: 'plot_river_c.png',
  river_o_0: 'plot_river_o_0.png',
  river_o_1: 'plot_river_o_1.png',
  river_o_2: 'plot_river_o_2.png',
  river_o_3: 'plot_river_o_3.png',
  river_o_4: 'plot_river_o_4.png',
  river_o_5: 'plot_river_o_5.png'
}
const isMobile = ref(false)

const setTextAllowOverlap = (map) => {
  const zoom = map.getZoom()
  map.setLayoutProperty('place-label-city', 'text-allow-overlap', zoom >= 8)
}

const getTopRankFeature = (features) => {
  return features.reduce((a, b) => {
    const rankA = Number(a.properties.rank ?? 0)
    const rankB = Number(b.properties.rank ?? 0)
    return rankA > rankB ? a : b
  })
}

export const useRiverMap = () => {
  const config = useRuntimeConfig()
  const loadedMap = shallowRef(null)
  const tmpZoomLevel = ref(0)
  const tmpMapCenter = ref([])
  if (import.meta.client) {
    isMobile.value = getSmartPlatform() !== ''
  }
  const parseData = (res) => {
    if (!res.data.basetimes || !res.data.urls?.tile) return {}

    const latestBasetime = last(res.data.basetimes)
    const latestTile = res.data.urls.tile[latestBasetime]
    return {
      d: `${config.public.wxTechDomain}${latestTile}`,
      tm: dayjs(latestBasetime).unix(),
      isForecast: false
    }
  }

  const updatePointIcon = (id) => {
    if (!loadedMap.value) return
    const map = loadedMap.value
    map.setFilter(
      'obsCamUnClusteredLayer-selected',
      ['==', ['get', 'path'], id]
    )
    map.setFilter(
      'obsCamUnClusteredLayer-selected-outline',
      ['==', ['get', 'path'], id]
    )
    map.setFilter(
      'obsCamUnClusteredLayer-unselected',
      ['!=', ['get', 'path'], id]
    )
  }

  const setHighlightArea = (citycode) => {
    if (!loadedMap.value) return
    const map = loadedMap.value
    map.setFilter(
      'highlight_city_line',
      ['==', ['get', 'N03_007'], citycode]
    )
    map.setFilter(
      'highlight_city_fill',
      ['!=', ['get', 'N03_007'], citycode]
    )
    if (citycode === 'none') {
      map.setFilter(
        'highlight_city_fill',
        ['==', ['get', 'N03_007'], citycode]
      )
      if (map.getLayer('reverse_japan')) {
        map.removeLayer('reverse_japan')
      }
      if (map.getSource('reverse_japan')) {
        map.removeSource('reverse_japan')
      }
      return
    }
    if (!map.getSource('reverse_japan')) {
      map.addSource('reverse_japan', {
        type: 'vector',
        tiles: [
          `${config.public.siteDataDomain}/map/vectortile/reverse_japan/{z}/{x}/{y}.pbf`
        ]
      })
    }
    if (!map.getLayer('reverse_japan')) {
      map.addLayer({
        'id': 'reverse_japan',
        'type': 'fill',
        'source': 'reverse_japan',
        'source-layer': 'reverse_japan',
        'minzoom': 3,
        'maxzoom': 16,
        'paint': {
          'fill-color': '#303030',
          'fill-opacity': 0.2
        }
      }, 'obsCamClusteredLayer')
    }
  }

  const getRiverRiskData = async () => {
    return new Promise((resolve, reject) => {
      axios.get(`${config.public.wxTechDomain}/tile/vector/wxbiz/kikikuru/flood_vector/latest.json`)
        .then((res) => {
          if (res.status === 200) {
            resolve(res)
          } else {
            reject(new Error('Failed to fetch river risk data'))
          }
          return res
        })
        .catch((error) => {
          console.error(error)
          reject(error)
        })
    })
  }

  const setRiverRisk = (res) => {
    if (!loadedMap.value) return
    const map = loadedMap.value
    const data = parseData(res)
    if (!data.d) return
    map.addSource('riverRisk', {
      ...res.data.options,
      tiles: [`${data.d}`]
    })
    map.addLayer({
      'id': 'riverRisk',
      'source': 'riverRisk',
      'type': 'line',
      'source-layer': 'flood',
      'layout': {
        visibility: 'visible'
      },
      'paint': {
        'line-opacity': 1,
        'line-width': [
          'interpolate',
          ['exponential', 0.5],
          ['zoom'],
          4, 1,
          7, 3,
          11, 4,
          16, 8
        ],
        'line-color': [
          'match',
          [
            'match',
            ['get', 'type'],
            2,
            ['-', ['get', 'risk'], 1],
            ['get', 'risk']
          ],
          1,
          'rgb(242, 231, 0)',
          2,
          'rgb(255, 40, 0)',
          3,
          'rgb(170, 0, 170)',
          4,
          'rgb(12, 0, 12)',
          'rgba(255, 255, 255, 0)'
        ]
      },
      'filter': [
        '==', ['get', 'type'], 2
      ]
    }, 'riverLabel')
  }

  const registerIconImages = async (map) => {
    for (const [key, url] of Object.entries(iconImages)) {
      if (map.hasImage(key)) continue
      const image = await map.loadImage(`${config.public.imgDomain}/onebox/img/river/map/${url}`)
      if (image) {
        map.addImage(key, image.data)
      } else {
        console.warn(`Failed to load image: ${url}`)
      }
    }
  }

  const setPopupContent = (map, e) => {
    const feature = e.features[0]
    const coordinates = [...feature.geometry.coordinates]
    const name = feature.properties.name
    let borderColor = '#ffffff'
    switch (feature.properties.rank) {
      case 2: {
        borderColor = '#F2E700'
        break
      }
      case 3: {
        borderColor = '#FF2900'
        break
      }
      case 4: {
        borderColor = '#AB01AA'
        break
      }
      case 5: {
        borderColor = '#0C000C'
        // No default
      }
        break
    }

    const path = feature.properties.path.replace(/^cctv_/, '')
    const link = feature.properties.rvr_sys_roma === 'others'
      ? `/?pid=${path}`
      : `/${feature.properties.rvr_sys_roma}/?pid=${path}`
    // ポップアップ生成
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 10
    })
      .setLngLat(coordinates)
      .setHTML(`<div>${name}</div>`)
      .addTo(map)
    const popupContent = popup.getElement() && popup.getElement().querySelector('.maplibregl-popup-content')
    if (popupContent) {
      popupContent.style.border = `4px solid ${borderColor}`
      popupContent.style.pointerEvents = 'auto'
      popupContent.addEventListener('mousedown', (e) => e.stopPropagation())
      popupContent.addEventListener('touchstart', (e) => {
        e.stopPropagation()
        navigateTo(link)
      })
    }
    popup._navigateLink = link
    return popup
  }

  const registerLayerMouseEvents = (map, layerId) => {
    let currentPopupFeatureId = null
    map.on('mouseenter', layerId, (e) => {
      map.getCanvas().style.cursor = 'pointer'
      if (!isMobile.value) {
        if (map._popup) map._popup.remove()
        if (e.features && e.features.length > 0) {
          const feature = getTopRankFeature(e.features)
          map._popup = setPopupContent(map, { ...e, features: [feature] })
        }
      }
    })
    map.on('mousemove', layerId, (e) => {
      map.getCanvas().style.cursor = 'pointer'
      if (!isMobile.value) {
        if (map._popup) map._popup.remove()
        if (e.features && e.features.length > 0) {
          const feature = getTopRankFeature(e.features)
          map._popup = setPopupContent(map, { ...e, features: [feature] })
        }
      }
    })
    map.on('mouseleave', layerId, () => {
      map.getCanvas().style.cursor = ''
      if (map._popup) {
        map._popup.remove()
        map._popup = null
        currentPopupFeatureId = null
      }
    })
    map.on('click', layerId, (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [layerId] })
      if (features.length > 0) {
        const feature = getTopRankFeature(features)
        if (isMobile.value) {
          const featureId = feature.id || feature.properties.id || JSON.stringify(feature.geometry.coordinates)
          // すでに同じfeatureのポップアップが出ている場合は遷移
          if (map._popup && currentPopupFeatureId === featureId && map._popup._navigateLink) {
            return navigateTo(map._popup._navigateLink)
          }
          // 別featureならポップアップを出し直す
          if (map._popup) map._popup.remove()
          map._popup = setPopupContent(map, { ...e, features: [feature] })
          currentPopupFeatureId = featureId
        } else {
          return navigateTo(map._popup._navigateLink)
        }
      } else {
      // 何もないところをクリックしたらポップアップ削除
        if (map._popup) {
          map._popup.remove()
          map._popup = null
          currentPopupFeatureId = null
        }
      }
    })
  }

  const addLocationMarker = async (map) => {
    const image = await map.loadImage(`${config.public.imgDomain}/golf/image/map/kaze_plot.png`)
    map.addImage('user-location-img', image.data)
    map.addSource('user-location', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [] }
          }
        ]
      }
    })
    map.addLayer({
      id: 'user-location-symbol',
      type: 'symbol',
      source: 'user-location',
      layout: {
        'icon-image': 'user-location-img',
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          3,
          0.25,
          11,
          0.4
        ],
        'icon-anchor': 'bottom'
      }
    })
  }

  const bezierEase = cubicBezier(0.57, 0.25, 0, 0.75)
  const easeOut = cubicBezier(0.16, 1, 0.3, 1)
  const createPulsingDot = (map, color) => {
    const size = 300
    return {
      width: size,
      height: size,
      data: new Uint8Array(size * size * 4),
      onAdd() {
        const canvas = document.createElement('canvas')
        canvas.width = this.width
        canvas.height = this.height
        this.context = canvas.getContext('2d')
      },
      render() {
        const animDuration = 1001
        const waitDuration = 499
        const totalDuration = animDuration + waitDuration
        const now = performance.now()
        const t = now % totalDuration
        const minRadius = 0
        const maxRadius = size / 2
        let tRadius, tAlpha
        if (t < animDuration) {
          const tRaw = t / animDuration
          if (tRaw < 1000 / 1001) {
            const bezT = bezierEase(tRaw / (1000 / 1001))
            tRadius = bezT
            tAlpha = 1 - bezT
          } else {
            tRadius = 1
            const fadeT = (tRaw - 1000 / 1001) / (1 / 1001)
            tAlpha = 1 - easeOut(fadeT)
          }
        } else {
          tRadius = 0
          tAlpha = 0
        }
        const currentRadius = minRadius + (maxRadius - minRadius) * tRadius
        const context = this.context
        context.clearRect(0, 0, this.width, this.height)
        if (tAlpha > 0) {
          context.beginPath()
          context.arc(this.width / 2, this.height / 2, currentRadius, 0, Math.PI * 2)
          context.fillStyle = `rgba(${color},${tAlpha})`
          context.fill()
        }
        this.data = context.getImageData(0, 0, this.width, this.height).data
        map.triggerRepaint()
        return true
      }
    }
  }

  const initMap = (mapContainer, mapZoom, mapCenter) => {
    return new Promise((resolve) => {
      let attributionControl = null
      const protocol = new Protocol()
      maplibregl.addProtocol('pmtiles', protocol.tile)
      const map = new maplibregl.Map({
        container: mapContainer,
        style: `${config.public.mapDomain}/style/global/osm/basic-ja.json`,
        localIdeographFontFamily: '"Noto Sans", "Noto Sans CJK SC", sans-serif',
        center: mapCenter,
        zoom: mapZoom,
        minZoom: 3,
        maxZoom: 16,
        maxBounds: [
          [122.5, 20],
          [152.5, 48]
        ],
        attributionControl: false,
        doubleClickZoom: false,
        dragRotate: false,
        keyboard: false,
        touchPitch: false,
        cooperativeGestures: isMobile.value,
        locale: {
          'CooperativeGesturesHandler.WindowsHelpText': '地図を移動するには、Ctrlキーを押しながらスクロールしてください。',
          'CooperativeGesturesHandler.MacHelpText': '地図を移動するには、⌘キーを押しながらスクロールしてください。',
          'CooperativeGesturesHandler.MobileHelpText': '地図を移動するには、2本指でドラッグしてください。'
        }
      })
      map.touchZoomRotate.disableRotation()
      attributionControl = new CustomAttributionControl({
        compact: true
      })
      map.addControl(attributionControl, 'bottom-left')
      map.addControl(new CustomLocationControl(), 'bottom-left')
      map.addControl(new maplibregl.NavigationControl({
        showCompass: false,
        showZoom: true
      }), 'bottom-left')
      map.once('load', async () => {
        tmpZoomLevel.value = map.getZoom()
        setTextAllowOverlap(map)
        await registerIconImages(map)
        map.addSource('riverLine', {
          type: 'vector',
          url: `${config.public.mapDomain}/json/jma/flood/latest.json`
        })
        map.addLayer({
          'id': 'riverEdge',
          'source': 'riverLine',
          'source-layer': 'flood',
          'type': 'line',
          'paint': {
            'line-color': '#000',
            'line-width': [
              'interpolate',
              ['exponential', 0.5],
              ['zoom'],
              5, 2,
              7, 4,
              11, 6,
              16, 10
            ]
          },
          'layout': {
            'line-join': 'round'
          },
          'filter': [
            '==', ['get', 'type'], 2
          ]
        })
        map.addLayer({
          'id': 'riverLine',
          'source': 'riverLine',
          'source-layer': 'flood',
          'type': 'line',
          'paint': {
            'line-color': '#9ca3af',
            'line-width': [
              'interpolate',
              ['exponential', 0.5],
              ['zoom'],
              5, 1,
              7, 3,
              11, 4,
              16, 8
            ]
          },
          'layout': {
            'line-cap': 'round',
            'line-join': 'round'
          },
          'filter': [
            '==', ['get', 'type'], 2
          ]
        })
        map.addLayer({
          'id': 'riverLabel',
          'source': 'riverLine',
          'source-layer': 'flood',
          'type': 'symbol',
          'layout': {
            'text-field': ['get', 'name'],
            'text-font': ['Noto Sans Regular', 'Arial Unicode MS Regular'],
            'text-size': 14,
            'symbol-placement': 'line',
            'text-rotation-alignment': 'map',
            'text-allow-overlap': true,
            'symbol-spacing': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 150,
              12, 250
            ]
          },
          'paint': {
            'text-color': '#1e293b',
            'text-halo-color': '#fff',
            'text-halo-width': 2,
            'text-opacity': [
              'step',
              ['zoom'],
              0, 10, 1
            ]
          },
          'filter': [
            '==', ['get', 'type'], 2
          ]
        })
        map.addSource('japan_city', {
          type: 'vector',
          tiles: [
            `${config.public.siteDataDomain}/map/vectortile/japan_city_20250101/{z}/{x}/{y}.pbf`
          ]
        })
        map.addLayer({
          'id': 'highlight_city_line',
          'type': 'line',
          'source': 'japan_city',
          'source-layer': 'japan_city',
          'minzoom': 3,
          'maxzoom': 16,
          'filter': [
            '==', ['get', 'N03_007'], ''
          ],
          'paint': {
            'line-color': '#B40000',
            'line-width': 2,
            'line-dasharray': [4, 2]
          }
        })
        map.addLayer({
          'id': 'highlight_city_fill',
          'type': 'fill',
          'source': 'japan_city',
          'source-layer': 'japan_city',
          'minzoom': 3,
          'maxzoom': 16,
          'filter': [
            '==', ['get', 'N03_007'], ''
          ],
          'paint': {
            'fill-color': '#303030',
            'fill-opacity': 0.2
          }
        })
        map.addSource('obsCamClusteredPoints', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          },
          cluster: false,
          generateId: true
        })
        map.addLayer({
          id: 'obsCamClusteredLayer',
          source: 'obsCamClusteredPoints',
          type: 'symbol',
          layout: {
            'icon-image': [
              'case',
              ['==', ['get', 'type'], 'cam'],
              'river_c',
              ['==', ['get', 'type'], 'obs'],
              [
                'concat',
                'river_o_',
                ['to-string', ['get', 'rank']]
              ],
              ''
            ],
            'icon-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              3,
              0.25,
              11,
              0.5
            ],
            'icon-allow-overlap': true
          }
        })
        map.addSource('obsCamUnClusteredPoints', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          },
          cluster: false,
          generateId: true
        })
        map.addLayer({
          id: 'obsCamUnClusteredLayer-unselected',
          source: 'obsCamUnClusteredPoints',
          type: 'symbol',
          layout: {
            'icon-image': [
              'case',
              ['==', ['get', 'type'], 'cam'],
              'river_c',
              ['==', ['get', 'type'], 'obs'],
              [
                'concat',
                'river_o_',
                ['to-string', ['get', 'rank']]
              ],
              ''
            ],
            'icon-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              3,
              0.25,
              11,
              0.5
            ],
            'icon-allow-overlap': true,
            'symbol-sort-key': ['get', 'rank']
          }
        })
        const pulsingDot = createPulsingDot(map, '53,105,192')
        if (!map.hasImage('pulsing-dot')) {
          map.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 })
        }
        map.addLayer({
          id: 'obsCamUnClusteredLayer-selected-outline',
          source: 'obsCamUnClusteredPoints',
          type: 'symbol',
          layout: {
            'icon-image': 'pulsing-dot',
            'icon-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              3, 0.4,
              11, 0.7
            ],
            'icon-allow-overlap': true
          },
          filter: [
            '==', ['get', 'type'], 'cam'
          ]
        })
        map.addLayer({
          id: 'obsCamUnClusteredLayer-selected',
          source: 'obsCamUnClusteredPoints',
          type: 'symbol',
          layout: {
            'icon-image': [
              'case',
              ['==', ['get', 'type'], 'cam'],
              'river_c',
              ['==', ['get', 'type'], 'obs'],
              [
                'concat',
                'river_o_',
                ['to-string', ['get', 'rank']]
              ],
              ''
            ],
            'icon-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              3,
              0.4,
              11,
              0.7
            ],
            'icon-allow-overlap': true,
            'symbol-sort-key': ['get', 'rank']
          }
        })
        await addLocationMarker(map)
        map.on('zoom', () => setTextAllowOverlap(map))
        map.on('moveend', () => {
          const center = map.getCenter()
          const zoom = map.getZoom()
          tmpMapCenter.value = [center.lng, center.lat]
          tmpZoomLevel.value = zoom
        })
        registerLayerMouseEvents(map, 'obsCamClusteredLayer')
        registerLayerMouseEvents(map, 'obsCamUnClusteredLayer-unselected')
        registerLayerMouseEvents(map, 'obsCamUnClusteredLayer-selected')
        const osmSource = map.getSource('osm')
        const attribution = osmSource.attribution
        if (attribution) {
          attributionControl.updateCustomAttributions([attribution])
        }
        loadedMap.value = map
        resolve()
      })
    })
  }
  return {
    initMap,
    getRiverRiskData,
    setRiverRisk,
    updatePointIcon,
    setHighlightArea,
    loadedMap,
    tmpZoomLevel,
    tmpMapCenter
  }
}
